"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type TimelineEntry = {
  year: string;
  yearItalic: boolean;
  chapter: string;
  heading: string;
  copy: string;
  image: string;
  srcset: string;
  sizes: string;
  alt: string;
};
const entries: TimelineEntry[] = [{
  year: "2018",
  yearItalic: false,
  chapter: "CHAPTER 01 · THE SEED",
  heading: "A vacant lot turned green.",
  copy: "Eco Grow began with a single mission: to transform an abandoned neighborhood lot into a thriving community space. Neighbors gathered with shovels and compost, clearing debris to lay the foundation for our first raised beds.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701353/img/garden_founding_2018.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701353/img/garden_founding_2018.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701353/img/garden_founding_2018.png-wvc-sizes",
  alt: "Community members clearing the original garden lot, 2018."
}, {
  year: "2019",
  yearItalic: true,
  chapter: "CHAPTER 02 · FIRST HARVEST",
  heading: "The season of abundance.",
  copy: "Our first full growing season brought a bounty of heirloom tomatoes, crisp kale, and vibrant radishes. The community table was set, and the first neighborhood harvest dinner became a cherished annual tradition.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701353/img/garden_harvest_2019.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701353/img/garden_harvest_2019.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701353/img/garden_harvest_2019.png-wvc-sizes",
  alt: "Freshly picked seasonal vegetables from the garden, 2019."
}, {
  year: "2020",
  yearItalic: false,
  chapter: "CHAPTER 03 · THE GREENHOUSE",
  heading: "Extending the growing cycle.",
  copy: "With the addition of our community greenhouse, we began starting seeds earlier in the spring and extending our harvest into the late autumn. It became a sanctuary for learning and winter propagation.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701353/img/garden_greenhouse_2020.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701353/img/garden_greenhouse_2020.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701353/img/garden_greenhouse_2020.webp-wvc-sizes",
  alt: "The community greenhouse filled with seedlings, 2020."
}, {
  year: "2021",
  yearItalic: true,
  chapter: "CHAPTER 04 · COMPOSTING",
  heading: "Closing the nutrient loop.",
  copy: "We launched our neighborhood composting initiative, turning kitchen scraps into rich, organic soil. This closed-loop system became the heart of our garden's health and our commitment to zero-waste living.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701353/img/garden_compost_2021.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701353/img/garden_compost_2021.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701353/img/garden_compost_2021.png-wvc-sizes",
  alt: "Community composting bins and organic soil, 2021."
}, {
  year: "2022",
  yearItalic: false,
  chapter: "CHAPTER 05 · WORKSHOPS",
  heading: "Sharing the craft of growth.",
  copy: "Our workshop series took root, offering hands-on lessons in organic pest management, seed saving, and seasonal planting. We empowered a new generation of gardeners to grow their own food with confidence.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701353/img/garden_workshops_2022.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701353/img/garden_workshops_2022.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701353/img/garden_workshops_2022.png-wvc-sizes",
  alt: "Workshop instructor teaching organic gardening techniques, 2022."
}, {
  year: "2024",
  yearItalic: true,
  chapter: "CHAPTER 06 · THE FUTURE",
  heading: "Growing together for good.",
  copy: "Today, Eco Grow is a vibrant hub of sustainability. With expanded plot rentals and community events, we continue to cultivate not just food, but deep connections between neighbors and the earth.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701353/img/garden_community_2024.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701353/img/garden_community_2024.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701353/img/garden_community_2024.png-wvc-sizes",
  alt: "Community members gathering in the garden, 2024."
}];
function TimelineRow({
  entry,
  index
}: {
  entry: TimelineEntry;
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15,
    rootMargin: "0px 0px -80px 0px"
  });
  const isLeft = index % 2 === 0;
  return <div ref={ref} data-index={index} className={`relative transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
      {}
      <div className="hidden md:block absolute left-1/2 top-10 -translate-x-1/2 size-3 rounded-lg z-10" style={{
      backgroundColor: "var(--chart-1)"
    }} aria-hidden="true" />

      <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-start">
        {}
        <div className={`${isLeft ? "md:pr-16 md:text-right order-1" : "md:pr-16 md:text-right order-1 md:order-1 md:invisible"}`}>
          {isLeft ? <TimelineContent entry={entry} alignRight /> : <TimelineImage entry={entry} />}
        </div>

        {}
        <div className={`${isLeft ? "md:pl-16 order-2" : "md:pl-16 order-2"}`}>
          {isLeft ? <TimelineImage entry={entry} /> : <TimelineContent entry={entry} alignRight={false} />}
        </div>
      </div>
    </div>;
}
function TimelineContent({
  entry,
  alignRight
}: {
  entry: TimelineEntry;
  alignRight: boolean;
}) {
  return <div className={`flex flex-col ${alignRight ? "md:items-end" : "md:items-start"} items-start`}>
      <h3 className={`font-default leading-[0.95] text-foreground text-5xl sm:text-6xl md:text-7xl mb-4 ${entry.yearItalic ? "italic" : ""}`}>
        {entry.year}
      </h3>
      <p className="font-mono text-xs tracking-[0.18em] mb-5" style={{
      color: "var(--chart-1)"
    }}>
        {entry.chapter}
      </p>
      <h4 className="font-default text-2xl md:text-[28px] leading-snug text-foreground mb-4 max-w-[380px]">
        {entry.heading}
      </h4>
      <p className={`font-default text-base leading-relaxed text-muted-foreground max-w-[380px] ${alignRight ? "md:text-right" : "md:text-left"} text-left`}>
        {entry.copy}
      </p>
    </div>;
}
function TimelineImage({
  entry
}: {
  entry: TimelineEntry;
}) {
  return <div className="rounded-lg overflow-hidden p-2 bg-background border border-border shadow-md w-full max-w-[380px]">
      <div className="overflow-hidden rounded-md">
        <img src={entry.image} data-wvc-srcset={entry.srcset} data-wvc-sizes={entry.sizes} alt={entry.alt} loading="lazy" decoding="async" className="w-full aspect-[4/3] object-cover" />
      </div>
    </div>;
}
export default function OurGrowthTimeline() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: spineRef,
    isVisible: spineVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.05,
    once: true
  });
  const {
    ref: footerRef,
    isVisible: footerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.4
  });
  return <section data-section-id="2431" id="our-growth-timeline" className="dark bg-background text-foreground py-32 md:py-44 lg:py-[180px] px-6 md:px-10 lg:px-16 overflow-hidden">
      <div className="max-w-6xl mx-auto">
        {}
        <div ref={headerRef} className={`mb-24 md:mb-32 max-w-[640px] transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="font-mono text-xs tracking-[0.22em] mb-8" style={{
          color: "var(--chart-1)"
        }}>
            OUR GROWTH
          </p>
          <h2 className="font-default text-5xl sm:text-6xl md:text-[64px] leading-[1.02] text-foreground mb-8">
            Years of
            <br />
            <span className="italic" style={{
            color: "var(--chart-4)"
          }}>
              cultivating community.
            </span>
          </h2>
          <p className="font-default text-lg leading-relaxed text-muted-foreground max-w-[560px]">
            From a neglected lot to a thriving neighborhood sanctuary, our journey is rooted in shared effort, organic growth, and the joy of the harvest. These are the milestones that define our garden's story.
          </p>
        </div>

        {}
        <div className="relative" ref={spineRef}>
          {}
          <div className="hidden md:block absolute left-1/2 top-0 -translate-x-1/2 w-px overflow-hidden" style={{
          height: "100%"
        }} aria-hidden="true">
            <div className={`w-[1.5px] bg-border origin-top transition-transform duration-[2000ms] ease-out`} style={{
            height: "100%",
            transform: spineVisible ? "scaleY(1)" : "scaleY(0)",
            backgroundColor: "var(--border)"
          }} />
          </div>

          {}
          <div className="md:hidden absolute left-3 top-0 w-px overflow-hidden" style={{
          height: "100%"
        }} aria-hidden="true">
            <div className="w-[1.5px] origin-top transition-transform duration-[2000ms] ease-out" style={{
            height: "100%",
            transform: spineVisible ? "scaleY(1)" : "scaleY(0)",
            backgroundColor: "var(--border)"
          }} />
          </div>

          <div className="flex flex-col gap-24 md:gap-[120px] md:pl-0 pl-10">
            {entries.map((entry, i) => <TimelineRow key={entry.year} entry={entry} index={i} />)}
          </div>
        </div>

        {}
        <div ref={footerRef} className={`mt-24 md:mt-32 flex justify-center transition-all duration-1000 ease-out ${footerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <p className="font-mono text-xs tracking-[0.22em] italic text-center" style={{
          color: "var(--chart-4)",
          opacity: 0.7
        }}>
            AND THE NEXT SEASON IS WAITING.
          </p>
        </div>
      </div>
    </section>;
}